/**
 * API Verification Manager
 *
 * Tracks whether at least one successful API call has been made.
 * This allows the app to show a loading screen until the user's
 * authorization has been verified by a successful API response.
 */

type VerificationListener = (verified: boolean) => void;

let isVerified = false;
const listeners: Set<VerificationListener> = new Set();

/**
 * Mark the API as verified (called on first successful response)
 */
export const markApiVerified = () => {
	if (!isVerified) {
		isVerified = true;
		listeners.forEach((listener) => listener(true));
	}
};

/**
 * Reset verification state (called on logout)
 */
export const resetApiVerification = () => {
	isVerified = false;
	listeners.forEach((listener) => listener(false));
};

/**
 * Check if API has been verified
 */
export const isApiVerified = (): boolean => isVerified;

/**
 * Subscribe to verification state changes
 */
export const subscribeToVerification = (
	listener: VerificationListener,
): (() => void) => {
	listeners.add(listener);
	// Immediately call with current state
	listener(isVerified);
	// Return unsubscribe function
	return () => listeners.delete(listener);
};
